import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs, onSnapshot } from 'firebase/firestore';
import Navbar from '../components/Navbar';
import { motion } from 'framer-motion';
import { FaCalendar, FaUser, FaArrowRight, FaTwitter, FaInstagram, FaBehance } from 'react-icons/fa';

const Blog = () => {
  const [blogs, setBlogs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    const blogCollection = collection(db, 'blogs');
    const unsubscribe = onSnapshot(blogCollection, (snapshot) => {
      const blogList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      blogList.sort((a, b) => new Date(b.date) - new Date(a.date));
      setBlogs(blogList);
    });

    return () => unsubscribe();
  }, []);

  return (
    <div className="min-h-screen bg-perspective-carbon text-perspective-sand relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 opacity-60">
        <div className="absolute inset-0 bg-grid-soft [background-size:28px_28px] opacity-20" />
      </div>

      <div className="relative z-10">
        <Navbar />
        <div className="h-16" />

        {/* Hero Section */}
        <section className="px-4 pt-12 pb-12 lg:pt-16 lg:pb-16">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-sm font-semibold text-perspective-sage uppercase tracking-[0.08em] mb-4">Our insights</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                Our Blog
              </h1>
              <p className="text-lg sm:text-xl text-perspective-cloud/85 max-w-3xl mx-auto leading-relaxed">
                Stay updated with the latest insights, trends, and tips from our team of digital experts.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Blog Posts Grid */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto">
            {blogs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {blogs.map((post, index) => (
                  <BlogCard key={index} {...post} />
                ))}
              </div>
            ) : (
              <motion.div 
                className="text-center py-16"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <div className="w-24 h-24 bg-perspective-sage/15 rounded-full flex items-center justify-center mx-auto mb-6 border border-perspective-sage/20">
                  <FaCalendar className="text-perspective-sage text-3xl" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">No Blog Posts Yet</h3>
                <p className="text-perspective-cloud/80 text-lg">We're working on creating amazing content for you. Check back soon!</p>
              </motion.div>
            )}
          </div>
        </section>

        {/* Newsletter Section */}
        <section className="px-4 py-16 lg:py-20">
          <div className="max-w-6xl mx-auto rounded-3xl overflow-hidden bg-gradient-to-r from-perspective-carbon to-perspective-border text-white shadow-elevated border border-perspective-sage/15">
            <div className="p-10 md:p-12 text-center">
              <p className="text-sm uppercase tracking-[0.12em] text-perspective-mint mb-4">Stay updated</p>
              <h2 className="text-3xl sm:text-4xl font-bold mb-6">Subscribe to Our Newsletter</h2>
              <p className="text-lg text-perspective-mint/90 mb-8 max-w-2xl mx-auto">
                Get the latest insights, trends, and updates delivered directly to your inbox.
              </p>
              <form className="max-w-2xl mx-auto flex flex-col sm:flex-row gap-4">
                <input 
                  type="email" 
                  placeholder="Enter your email address"
                  className="flex-1 px-6 py-4 rounded-full bg-white/5 border border-white/10 text-white placeholder-perspective-cloud/50 focus:outline-none focus:border-perspective-sage focus:ring-2 focus:ring-perspective-sage/20 transition-all duration-300"
                />
                <button className="group bg-perspective-sage text-perspective-carbon px-8 py-4 rounded-full font-semibold hover:scale-105 transition-all duration-300 shadow-lg flex items-center justify-center gap-2">
                  Subscribe
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-perspective-carbon text-white border-t border-white/10">
          <div className="max-w-6xl mx-auto px-4 py-14">
            <div className="grid md:grid-cols-4 gap-8 mb-10">
              <div className="md:col-span-2">
                <img 
                  src="/perspectivelogo.png" 
                  alt="Perspective HQ" 
                  className="h-8 w-auto mb-4"
                />
                <p className="text-perspective-mint/90 max-w-xl mb-6">
                  Creating digital experiences that drive growth and innovation. We're passionate about building solutions that make a difference.
                </p>
                <div className="flex gap-3">
                  <a href="https://twitter.com/perspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaTwitter size={16} />
                  </a>
                  <a href="https://instagram.com/theperspectivehq" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaInstagram size={16} />
                  </a>
                  <a href="https://www.behance.net/skaina" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-perspective-sage hover:text-perspective-carbon transition-colors">
                    <FaBehance size={16} />
                  </a>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Navigate</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <button onClick={() => navigate('/work')} className="block hover:text-perspective-sage transition-colors">Work</button>
                  <button onClick={() => navigate('/services')} className="block hover:text-perspective-sage transition-colors">Services</button>
                  <button onClick={() => navigate('/about')} className="block hover:text-perspective-sage transition-colors">About</button>
                  <button onClick={() => navigate('/blog')} className="block hover:text-perspective-sage transition-colors">Blog</button>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-4">Contact</h4>
                <div className="space-y-2 text-perspective-mint/90">
                  <p>info@perspectivehq.co.za</p>
                  <p>+27 69 339 5619</p>
                  <p>Durban, South Africa</p>
                </div>
              </div>
            </div>
            <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-4 text-sm text-perspective-mint/80">
              <p>© 2021-2025 Perspective HQ. All rights reserved.</p>
              <div className="flex gap-4">
                <button onClick={() => navigate('/privacy-policy')} className="hover:text-perspective-sage transition-colors">Privacy Policy</button>
                <button onClick={() => navigate('/terms-of-service')} className="hover:text-perspective-sage transition-colors">Terms of Service</button>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

const BlogCard = ({ id, title, excerpt, image, date, author, category }) => {
  return (
    <motion.div
      className="group bg-white/5 backdrop-blur border border-white/10 rounded-3xl shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      whileHover={{ y: -8 }}
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={image}
          alt={title}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-perspective-carbon/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute top-4 left-4">
          <span className="bg-perspective-sage/90 text-perspective-carbon px-3 py-1 rounded-full text-sm font-medium border border-perspective-sage/20">
            {category}
          </span>
        </div>
      </div>
      
      <div className="p-6">
        <h3 className="text-2xl font-bold mb-4 text-white group-hover:text-perspective-sage transition-colors leading-tight">
          {title}
        </h3>
        <p className="text-perspective-cloud/80 mb-6 leading-relaxed">{excerpt}</p>
        
        <div className="flex items-center gap-6 text-sm text-perspective-cloud/70 mb-6">
          <div className="flex items-center gap-2">
            <FaCalendar className="text-perspective-sage" />
            <span>{date}</span>
          </div>
          <div className="flex items-center gap-2">
            <FaUser className="text-perspective-sage" />
            <span>{author}</span>
          </div>
        </div>
        
        <Link 
          to={`/blog/${id}`} 
          className="inline-flex items-center gap-2 text-perspective-sage font-semibold group-hover:gap-4 transition-all duration-300"
        >
          Read More 
          <FaArrowRight className="text-sm group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>
    </motion.div>
  );
};

export default Blog;
